import React, { useEffect, useRef, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface WidgetPreviewProps {
  widgetIdToPreview: string;
  headerColor: string;
  primaryColor: string;
  widgetTitle: string;
  welcomeMessage: string;
  widgetPosition: 'bottom-right' | 'bottom-left';
  loaderScriptSrc?: string;
  platformApiBaseUrl?: string;
}

const resolveEnv = (key: string): string | undefined => {
  // Prefer process.env if present
  try {
    if (typeof process !== 'undefined' && (process as any).env && (process as any).env[key]) {
      return (process as any).env[key];
    }
  } catch {}
  // Then a global window object (app shell can inject)
  try {
    if (typeof window !== 'undefined' && (window as any).__DOPOMOGAI_ENV__ && (window as any).__DOPOMOGAI_ENV__[key]) {
      return (window as any).__DOPOMOGAI_ENV__[key];
    }
  } catch {}
  return undefined;
};

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  widgetIdToPreview,
  headerColor,
  primaryColor,
  widgetTitle,
  welcomeMessage,
  widgetPosition,
  loaderScriptSrc,
  platformApiBaseUrl,
}) => {
  const { t } = useTranslation('agentBuilder');
  const mountPointRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const previewIdSuffix = useId();
  const mountPointId = `widget-preview-mount-${previewIdSuffix.replace(/:/g, '')}`;
  const [scriptLoadKey, setScriptLoadKey] = useState(0);

  const resolvedApiBase =
    platformApiBaseUrl ??
    resolveEnv('VITE_API_URL') ??
    resolveEnv('NX_API_BASE_URL') ??
    (typeof window !== 'undefined' ? (window as any).__DOPOMOGAI_API_BASE__ : undefined) ??
    '';

  const resolvedLoaderSrc =
    loaderScriptSrc ??
    resolveEnv('VITE_WIDGET_LOADER_SRC') ??
    (resolvedApiBase ? `${resolvedApiBase.replace(/\/$/, '')}/static/loader-v2.js` : '');

  useEffect(() => {
    const mountPoint = mountPointRef.current;
    if (scriptRef.current) {
      scriptRef.current.remove();
      scriptRef.current = null;
    }
    if (mountPoint) {
      while (mountPoint.firstChild) {
        mountPoint.removeChild(mountPoint.firstChild);
      }
    }

    if (!widgetIdToPreview || !mountPoint || !resolvedLoaderSrc) {
      return;
    }

    const script = document.createElement('script');
    script.id = `widget-loader-script-${previewIdSuffix.replace(/:/g, '')}`;
    script.src = resolvedLoaderSrc;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-widget-id', widgetIdToPreview);
    script.setAttribute('data-mount-id', mountPointId);
    if (resolvedApiBase) {
      script.setAttribute('data-platform-api-base', resolvedApiBase);
    }
    script.setAttribute('data-header-color', headerColor);
    script.setAttribute('data-primary-color', primaryColor);
    script.setAttribute('data-widget-title', widgetTitle);

    document.body.appendChild(script);
    scriptRef.current = script;

    mountPoint.style.setProperty('--widget-header-color', headerColor);
    mountPoint.style.setProperty('--widget-primary-color', primaryColor);

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
      if (mountPointRef.current) {
        while (mountPointRef.current.firstChild) {
          mountPointRef.current.removeChild(mountPointRef.current.firstChild);
        }
      }
    };
  }, [
    widgetIdToPreview,
    headerColor,
    primaryColor,
    widgetTitle,
    scriptLoadKey,
    resolvedLoaderSrc,
    resolvedApiBase,
    mountPointId,
    previewIdSuffix,
  ]);

  const forceReloadWidget = () => {
    setScriptLoadKey(prevKey => prevKey + 1);
  };

  return (
    <div
      className={clsx(
        'relative w-full h-[500px] bg-slate-200 border border-slate-300 rounded-lg overflow-hidden shadow-inner',
        'flex items-center justify-center',
      )}
    >
      {!resolvedLoaderSrc && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-20">
          <div className="text-center text-sm text-slate-600">
            <p className="font-semibold mb-1">{t('step4.previewPane.loaderMissingTitle', 'Loader script not configured')}</p>
            <p>
              {t(
                'step4.previewPane.loaderMissingDescription',
                'Provide a loader script URL via props or VITE_WIDGET_LOADER_SRC to view the live preview.',
              )}
            </p>
          </div>
        </div>
      )}

      <div id={mountPointId} ref={mountPointRef} className="w-full h-full relative">
        {!widgetIdToPreview && (
          <div className="p-4 text-center text-slate-500">
            <p>{t('step4.previewPane.noWidgetId')}</p>
          </div>
        )}
      </div>

      <div
        className={clsx(
          'absolute w-16 h-16 bg-gradient-to-br rounded-full shadow-xl pointer-events-none',
          'flex items-center justify-center text-white',
          widgetPosition === 'bottom-right' ? 'bottom-4 right-4' : 'bottom-4 left-4',
        )}
        style={{
          backgroundColor: primaryColor,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
        </svg>
      </div>

      <button
        onClick={forceReloadWidget}
        className="absolute top-3 left-3 z-10 bg-white border-2 border-black text-xs font-semibold px-3 py-1 rounded-md shadow-[2px_2px_0_0_#000]"
        type="button"
      >
        {t('step4.previewPane.reloadButton', 'Reload Preview')}
      </button>
    </div>
  );
};